import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../home-landing/components/Footer';
import { Mail, Linkedin, Github } from 'lucide-react';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About - Joe Graziano | Professional Insights & Expertise</title>
        <meta name="description" content="Explore professional insights, industry knowledge, and expert perspectives on web development, design, career growth, and technology trends." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-16 max-w-4xl">
          {/* Hero Section */}
          <section className="mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              About Me
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">I’m a cybersecurity professional driven by curiosity and experience, building defenses that protect people and organizations from evolving threats.

            </p>
          </section>

          {/* Bio Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">My Story</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>I started my career in email security fresh out of university and have spent the past 15+ years working hands-on to protect global enterprises from phishing, malware, and emerging email-borne threats. As the threat landscape evolved, so did my work—adapting detection strategies, tools, and approaches to stay ahead of increasingly sophisticated attacks.

              </p>
              <p>Throughout my career, I’ve focused on practical security: building and refining detections, reducing false positives, and helping teams deliver solutions that work reliably at scale. I’ve led technical efforts, mentored colleagues, and contributed detection logic that directly improved customer protection in real-world environments.

              </p>
              <p>What’s kept me engaged all these years is the constant challenge. Attackers adapt, tools evolve, and effective security requires both deep technical understanding and sound judgment. I bring that mindset into every role—staying curious, grounded, and focused on building defenses that make a real difference.

              </p>
            </div>
          </section>

          {/* Skills & Expertise */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">What I Do</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-3">Security Engineering</h3>
                <p className="text-muted-foreground">Designing and improving security systems that protect organizations from phishing, malware, and emerging email-borne threats at scale.

                </p>
              </div>
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-3">Threat Detection & Response</h3>
                <p className="text-muted-foreground">Building accurate, behavior-based detections and investigating complex threats to reduce risk and improve real-world protection.

                </p>
              </div>
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-3">Detection Quality & Accuracy</h3>
                <p className="text-muted-foreground">Optimizing detection logic to reduce false positives, improve signal quality, and maintain trust in security platforms.

                </p>
              </div>
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-3">Technical Leadership</h3>
                <p className="text-muted-foreground">Leading initiatives, mentoring engineers, and collaborating across teams to deliver reliable, production-ready security solutions.

                </p>
              </div>
            </div>
          </section>

          {/* Connect Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">Let's Connect</h2>
            <p className="text-muted-foreground mb-6">
              I'm always interested in hearing about new projects and opportunities. Feel free to reach out!
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:joseph@joegraziano.ca"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">

                <Mail size={20} />
                Email Me
              </a>
              <a
                href="https://linkedin.com/in/joseph-graziano-34a44530"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-card text-foreground border border-border rounded-lg hover:bg-accent transition-colors">

                <Linkedin size={20} />
                LinkedIn
              </a>
              <a
                href="https://github.com/CySec-JoeSchmo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-card text-foreground border border-border rounded-lg hover:bg-accent transition-colors">

                <Github size={20} />
                GitHub
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>);

};

export default About;